import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { ErrorBanner, Empty, Badge } from '../../components/ui/Primitives.js';
import { Section } from '../../components/ui/Form.js';
import { relativeTime } from '../../lib/format.js';
import { FeatureGate } from './FeatureGate.js';
import { useFeatureCodes } from '../../lib/api/hooks-tms.js';
import { DEFAULT_FEATURE_CODES } from '@arcnet/contracts';
import type { Extension } from '@arcnet/contracts';

/**
 * Hot desking for this person: their PIN, where they are sitting, where they sat.
 *
 * On the extension because the PIN belongs to a person. Which handsets are
 * shared desks is a property of the handset, and lives with the handsets.
 */

interface HotDeskState {
  included: boolean;
  has_pin: boolean;
  session: { id: string; started_at: string; device_label: string | null; sip_username: string | null } | null;
  recent: Array<{
    id: string; started_at: string; ended_at: string | null; ended_reason: string | null;
    device_label: string | null; sip_username: string | null;
  }>;
}

const ENDED: Record<string, string> = {
  logout: 'logged out', displaced: 'logged in elsewhere',
  forced: 'ended by an administrator', expired: 'expired',
};

export function ExtensionHotDeskTab({ tenantId, ext, scope }: {
  tenantId: string; ext: Extension; scope: 'platform' | 'tenant';
}) {
  const qc = useQueryClient();
  const key = ['tenants', tenantId, 'extensions', ext.id, 'hot-desk'] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<HotDeskState>(`/v1/tenants/${tenantId}/extensions/${ext.id}/hot-desk`),
  });
  const [pin, setPin] = useState('');
  // The account's own codes, so the instruction matches what its phones answer.
  const codes = useFeatureCodes(tenantId).data?.codes ?? DEFAULT_FEATURE_CODES;

  const savePin = useMutation({
    mutationFn: (value: string | null) =>
      api.put(`/v1/tenants/${tenantId}/hot-desk/pin/${ext.id}`, { pin: value }),
    onSuccess: () => { setPin(''); void qc.invalidateQueries({ queryKey: key }); },
  });
  const endSession = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/hot-desk/sessions/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  if (q.error) return <ErrorBanner error={q.error} />;
  const s = q.data;
  const desk = (d: { device_label: string | null; sip_username: string | null }) =>
    d.device_label ?? d.sip_username ?? 'a shared desk';

  return (
    <FeatureGate tenantId={tenantId} extensionId={ext.id} scope={scope}
      feature="hot_desk" name="Hot desking">
      <Section title="PIN" columns={1}
        description={`At a shared desk phone, dial ${codes.hotdesk_login}${ext.number}* then this PIN to sit down as this extension; ${codes.hotdesk_logout} logs out.`}>
        <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Badge tone={s?.has_pin ? 'ok' : 'muted'}>{s?.has_pin ? 'PIN set' : 'no PIN'}</Badge>
          {/* Write-only: the PIN is stored as a hash and never shown. */}
          <input className="arc-input arc-mono" type="password" inputMode="numeric"
            autoComplete="new-password" style={{ maxWidth: 160 }}
            placeholder={s?.has_pin ? 'New PIN' : 'Choose a PIN'} value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} />
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
            disabled={pin.length < 4 || savePin.isPending}
            onClick={() => savePin.mutate(pin)}>
            {s?.has_pin ? 'Change PIN' : 'Set PIN'}
          </button>
          {s?.has_pin && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              disabled={savePin.isPending}
              title="Stops this extension logging in anywhere, and ends any login now"
              onClick={() => {
                if (confirm('Clear the PIN? This extension will be logged out of any shared desk and cannot log in again until a new PIN is set.')) {
                  savePin.mutate(null);
                }
              }}>
              Clear PIN
            </button>
          )}
        </div>
      </Section>

      <Section title="Sitting at" columns={1}>
        {s?.session ? (
          <div className="arc-row" style={{ gap: 10 }}>
            <span style={{ fontWeight: 550 }}>{desk(s.session)}</span>
            <span className="arc-subtle">since {relativeTime(s.session.started_at)}</span>
            <div className="arc-spacer" />
            <button type="button" className="arc-btn arc-btn-sm"
              disabled={endSession.isPending}
              onClick={() => endSession.mutate(s.session!.id)}>
              Log out
            </button>
          </div>
        ) : (
          <span className="arc-subtle">Not logged in at a shared desk.</span>
        )}
      </Section>

      <div className="arc-card">
        <div className="arc-card-head"><h2 className="arc-h2">Recent logins</h2></div>
        {(s?.recent.length ?? 0) === 0 ? (
          <Empty icon="phone" title="No logins yet"
            hint="Logins at shared desk phones appear here." />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead><tr><th>Desk</th><th>From</th><th>Ended</th></tr></thead>
              <tbody>
                {s!.recent.map((r) => (
                  <tr key={r.id}>
                    <td>{desk(r)}</td>
                    <td className="arc-muted">{relativeTime(r.started_at)}</td>
                    <td className="arc-muted">
                      {r.ended_at
                        ? <>{relativeTime(r.ended_at)} · {ENDED[r.ended_reason ?? ''] ?? r.ended_reason}</>
                        : <Badge tone="ok">still there</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </FeatureGate>
  );
}
